// The Solar System view's own face for the sim's warp level row.
//
// A second face, never a second copy: every level, every label and the picked
// index arrive through the config, and a click hands the pick straight back.
// Nothing here decides when a level may be reached or what drops one - those
// rules live with the sim, and whatever it does with them shows up here.

export interface WarpRowLevel {
  readonly rate: number;
  readonly label: string;
  readonly short: string;
}

/** One button after the ladder that warps to an instant and stops there. */
export interface WarpRowGoal {
  readonly id: string;
  readonly label: string;
  readonly title: string;
  visible(): boolean;
  /** Why a press would be refused right now, or null. */
  reason(): string | null;
  /** True while its run holds the clock. */
  active(): boolean;
  /** Start the run, or end the one in hand; the refusal, or null. */
  press(): string | null;
}

export interface WarpRowConfig {
  host: HTMLElement;
  levels: () => readonly WarpRowLevel[];
  pick: () => number;
  label: () => string;
  /**
   * Whether a level (an index into `levels()`) could be picked right now.
   * Asked per button, never once for the whole row: a rule that only blocks
   * some of the levels reads here exactly like one that blocks all of them,
   * and the row paints either shape with no change of its own.
   */
  reachable: (idx: number) => boolean;
  /** Why `reachable(idx)` is false right now, or null when it is true. A
   *  standing fact about the hold, not the transient drop notice below. */
  reason: (idx: number) => string | null;
  notice: () => string | null;
  setPick: (idx: number) => void;
  /** The rungs of the ladder this row belongs on. */
  visible: () => boolean;
  goal?: WarpRowGoal;
}

export interface WarpRowHandle {
  readonly el: HTMLElement;
  /** Repaint from the sim's own state; memoized, so a still frame writes nothing. */
  paint(): void;
  dispose(): void;
}

export function createWarpRow(config: WarpRowConfig): WarpRowHandle {
  const el = document.createElement('div');
  el.className = 'atlas-warp';
  config.host.appendChild(el);

  let paintedKey = '';
  let builtKey = '';
  const goal = config.goal ?? null;
  /** A press's refusal, standing under the row for a moment. */
  let refusal: { text: string; untilMs: number } | null = null;

  function paint(): void {
    const shown = config.visible();
    const levels = config.levels();
    const pick = config.pick();
    const label = config.label();
    const notice = config.notice();
    const reach = levels.map((_l, i) => config.reachable(i));
    const reasons = levels.map((_l, i) => config.reason(i));
    // One line under the row when nothing else is standing there: the first
    // reason any level is carrying, since today they all carry the same one
    // and a rule that differs per level still has ONE cause worth naming.
    const standingReason = reasons.find((r) => r !== null) ?? null;
    const goalShown = goal !== null && goal.visible();
    const goalReason = goalShown ? goal.reason() : null;
    const goalActive = goalShown && goal.active();
    if (refusal !== null && performance.now() > refusal.untilMs) refusal = null;
    const refused = goalShown ? refusal?.text ?? null : null;
    const key = `${shown ? 1 : 0}|${levels.map((l) => `${l.rate}:${l.short}`).join(',')}`
      + `|${pick}|${reach.join('')}|${standingReason ?? ''}|${label}|${notice ?? ''}`
      + `|${goalShown ? 1 : 0}${goalActive ? 1 : 0}|${goalReason ?? ''}|${refused ?? ''}`;
    if (key === paintedKey) return;
    paintedKey = key;
    el.classList.toggle('hidden', !shown);
    if (!shown) return;
    // The buttons are rebuilt only when the ladder itself changes: a click
    // lands on the element it was pressed on, and a repaint on every rate
    // sample would take it out from under the cursor.
    const build = levels.map((l) => `${l.rate}:${l.short}`).join(',');
    if (build !== builtKey) {
      builtKey = build;
      el.innerHTML = `<span class="atlas-warp-label" id="atlas-warp-label"></span>`
        + `<span class="atlas-warp-bar" id="atlas-warp-bar">${levels.map((l, i) => (
          `<button class="atlas-warp-btn" data-warp="${i}" data-value="${l.rate}"`
          + ` title="${l.label}">${l.short}</button>`
        )).join('')}</span>`
        + (goal ? `<button class="atlas-warp-goal" id="${goal.id}" data-goal="1">${goal.label}</button>` : '')
        + `<span class="atlas-warp-notice hidden" id="atlas-warp-notice"></span>`;
    }
    const elLabel = el.querySelector('#atlas-warp-label');
    if (elLabel) elLabel.textContent = label;
    // The picked level stays lit whether or not it can be reached right now -
    // locking is about what a PRESS does, never about which button is true.
    el.querySelectorAll<HTMLButtonElement>('[data-warp]').forEach((btn) => {
      const idx = Number(btn.dataset['warp']);
      const lv = levels[idx];
      btn.classList.toggle('active', idx === pick);
      btn.classList.toggle('blocked', !(reach[idx] ?? true));
      const r = reasons[idx] ?? null;
      if (lv) btn.title = r ? `${lv.label} - ${r}` : lv.label;
    });
    const elGoal = goal ? el.querySelector<HTMLButtonElement>(`#${goal.id}`) : null;
    if (goal && elGoal) {
      elGoal.classList.toggle('hidden', !goalShown);
      elGoal.classList.toggle('active', goalActive);
      elGoal.classList.toggle('blocked', goalReason !== null);
      elGoal.title = goalActive ? `${goal.title} - press to stop`
        : (goalReason ? `${goal.title} - ${goalReason}` : goal.title);
    }
    const elNotice = el.querySelector('#atlas-warp-notice');
    if (elNotice) {
      // A transient drop notice always wins; the standing reason only shows
      // in the gap a notice leaves, so a lock with nothing to say about
      // itself is not silently blank the way a global flag left it before.
      const transient = notice ?? refused;
      const showTransient = transient !== null;
      const showStanding = !showTransient && standingReason !== null;
      elNotice.classList.toggle('hidden', !showTransient && !showStanding);
      elNotice.classList.toggle('standing', showStanding);
      elNotice.textContent = showTransient ? transient : (showStanding ? `LOCKED: ${standingReason}` : '');
    }
  }

  function onClick(e: MouseEvent): void {
    if (goal && (e.target as HTMLElement | null)?.closest('[data-goal]')) {
      const text = goal.press();
      refusal = text === null ? null : { text, untilMs: performance.now() + 3000 };
      paint();
      return;
    }
    const btn = (e.target as HTMLElement | null)?.closest('[data-warp]') as HTMLElement | null;
    if (!btn) return;
    const idx = Number(btn.dataset['warp']);
    if (!Number.isFinite(idx)) return;
    config.setPick(idx);
    paint();
  }

  el.addEventListener('click', onClick);
  paint();

  return {
    el,
    paint,
    dispose(): void {
      el.removeEventListener('click', onClick);
      el.remove();
    },
  };
}
